import { useState } from 'react'
import { Info } from 'lucide-react'

import { Badge } from '@/common/ui/badge'
import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { cn } from '@/common/lib/utils'

type WhereKey = 'tag' | 'promotion' | 'game' | 'provider'

/** ลำดับเดียวกับ `findGamePreset` ฝั่ง auto-server (1 = บนสุด) */
const WHERE_TO_STEP: Record<WhereKey, number> = {
	promotion: 3,
	tag: 4,
	game: 5,
	provider: 6,
}

/**
 * คำอธิบายสอดคล้อง UI อื่น: โปรโมชั่น, แท็กลูกค้า, เทิร์นโอเวอร์, ค่ายเกม, รายละเอียดสมาชิก/ผู้เล่น
 */
const PRIORITY_STEPS: readonly { step: number; text: string }[] = [
	{ step: 1, text: 'กำหนดบนผู้เล่นโดยตรง (รายละเอียดสมาชิก/ผู้เล่น)' },
	{
		step: 2,
		text: 'Preset บังคับ (เช่น แคมเปญ หรือ ข้อความพิเศษ ที่ส่ง Preset มา)',
	},
	{
		step: 3,
		text: 'โปรโมชั่น (ฝาก/รับโบนัส — กระเป๋า / ทำเทิร์นโอเวอร์) ขณะรับสิทธิ์/ยังเคลียร์เทิร์นไม่ครบ',
	},
	{ step: 4, text: 'แท็กลูกค้า (หลายแท็ก: รายที่ได้ ล่าสุด ก่อน)' },
	{ step: 5, text: 'RTP ตั้งต้น รายเกม (รายละเอียดรายชื่อเกม)' },
	{
		step: 6,
		text: 'RTP ตั้งต้น ทั้งค่ายเกม (รายละเอียดรายชื่อค่าย — กรณี รายเกม ยังไม่กำหนดค่า)',
	},
] as const

const WHERE_INTRO: Record<WhereKey, { lead: string; more: string }> = {
	promotion: {
		lead: 'หน้าที่กำลังตั้ง: ลำดับ 3 — โปรโมชั่น / กระเป๋าโบนัส (ขณะรับสิทธิ์/ทำเทิร์นโอเวอร์)',
		more: 'ลำดับ 1-2 มีอันดับก่อน 3; 3 ก่อน 4-5-6 เมื่อ 1-2 ไม่บังคับ Preset หรือไม่ใช้',
	},
	tag: {
		lead: 'หน้าที่กำลังตั้ง: ลำดับ 4 — แท็กลูกค้า',
		more: 'ราย 1-3 มีอันดับก่อน 4; โปรฯ/กระเป๋า ยังรับ/ทำเทิร์นโอเวอร์ อยู่ จะก่อน Preset แท็ก, RTP รายเกม และทั้งค่าย',
	},
	game: {
		lead: 'หน้าที่กำลังตั้ง: ลำดับ 5 — RTP ตั้งต้น รายเกม',
		more: 'หลังเช็ก 1-4 แล้ว: 5 ก่อน 6 เมื่อลำดับก่อนไม่กำหนดค่า/ไม่บังคับ/ไม่ใช้',
	},
	provider: {
		lead: 'หน้าที่กำลังตั้ง: ลำดับ 6 — RTP ตั้งต้น ทั้งค่ายเกม (สำรองสุดท้าย)',
		more: 'หลังเช็ก 1-5 แล้ว: ใช้รายนี้กรณี รายเกม ยังไม่กำหนดค่า/ไม่มี/ไม่ใช้ หรือ ลำดับก่อนไม่บังคับ Preset',
	},
} as const

const dialogDescId = 'preset-priority-dialog-desc'

const triggerLabel = 'ดูลำดับการใช้ Preset / RTP'

type PresetPriorityHintProps = {
	/** จุดที่ Admin กำลังตั้งค่า */
	where: WhereKey
	className?: string
	/**
	 * - block: แถวเต็มใต้หัวข้อ มองเห็นชัด
	 * - default: แบบ outline เล็ก
	 * - icon: ไอคอน (dialog แก้เกม/ค่าย)
	 */
	trigger?: 'block' | 'default' | 'icon'
}

export function PresetPriorityHint({
	where,
	className,
	trigger = 'block',
}: PresetPriorityHintProps) {
	const [open, setOpen] = useState(false)
	const currentStep = WHERE_TO_STEP[where]
	const intro = WHERE_INTRO[where]

	return (
		<div
			className={cn(trigger === 'block' ? 'w-full' : 'inline-flex', className)}
		>
			<Button
				type="button"
				variant="outline"
				size={trigger === 'icon' ? 'icon' : 'default'}
				aria-label={triggerLabel}
				aria-expanded={open}
				aria-haspopup="dialog"
				className={cn(
					'gap-2',
					trigger === 'icon' &&
						'shrink-0 border-sky-200 bg-sky-50/80 text-sky-800 hover:bg-sky-100/80 dark:border-sky-800 dark:bg-sky-950/50 dark:text-sky-100 dark:hover:bg-sky-900/50',
					trigger === 'default' &&
						'h-9 border-sky-200 bg-sky-50/80 px-3 text-sm font-medium text-foreground shadow-sm hover:bg-sky-100/80 dark:border-sky-800 dark:bg-sky-950/50 dark:hover:bg-sky-900/50',
					trigger === 'block' &&
						'h-10 w-full justify-center border-sky-200 bg-sky-50/80 text-sm font-medium text-foreground shadow-sm hover:bg-sky-100/80 dark:border-sky-800 dark:bg-sky-950/50 dark:hover:bg-sky-900/50',
				)}
				onClick={() => setOpen(true)}
			>
				<Info
					className={cn('shrink-0 text-sky-600 dark:text-sky-400', {
						'h-4 w-4': trigger === 'icon' || trigger === 'default',
						'h-4 w-4 sm:h-5 sm:w-5': trigger === 'block',
					})}
					aria-hidden
				/>
				{trigger === 'default' && (
					<span className="whitespace-nowrap">{triggerLabel}</span>
				)}
				{trigger === 'block' && <span>{triggerLabel}</span>}
			</Button>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent
					className="max-h-[min(88vh,560px)] overflow-y-auto sm:max-w-md"
					onOpenAutoFocus={(e) => e.preventDefault()}
					aria-describedby={dialogDescId}
				>
					<DialogHeader>
						<DialogTitle>ลำดับการใช้ Preset / RTP</DialogTitle>
						<DialogDescription
							id={dialogDescId}
							className="space-y-2 pt-0.5 text-left text-foreground"
						>
							<p className="text-sm font-medium leading-relaxed text-foreground">
								{intro.lead}
							</p>
							<p className="text-xs leading-relaxed text-muted-foreground">
								{intro.more}
							</p>
						</DialogDescription>
					</DialogHeader>
					<div className="pt-1">
						<p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
							ลำดับทั้งหมด (บนสุดใช้ก่อน)
						</p>
						<ol className="list-none space-y-2.5 p-0">
							{PRIORITY_STEPS.map((row) => {
								const isCurrent = currentStep === row.step
								return (
									<li
										key={row.step}
										className={cn(
											'rounded-md border p-2.5 transition-colors',
											isCurrent
												? 'border-sky-500/90 bg-sky-100/90 dark:border-sky-500 dark:bg-sky-950/60'
												: 'border-border/50 bg-muted/20',
										)}
									>
										<div className="flex flex-wrap items-start justify-between gap-2">
											<p
												className={cn(
													'min-w-0 flex-1 text-sm leading-relaxed',
													isCurrent
														? 'font-medium text-foreground'
														: 'text-muted-foreground',
												)}
											>
												<span className="inline-block w-6 tabular-nums text-muted-foreground">
													{row.step}.
												</span>
												{row.text}
											</p>
											{isCurrent ? (
												<Badge
													variant="secondary"
													className="shrink-0 border-sky-300/80 bg-sky-600 text-xs font-medium text-white hover:bg-sky-600 dark:border-sky-500 dark:bg-sky-500"
												>
													หน้าที่กำลังตั้ง
												</Badge>
											) : null}
										</div>
									</li>
								)
							})}
						</ol>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	)
}
